import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

import { ListRunsStatusEnum, PlatformSyncStatusDTO, SyncRunResultDTO } from "@app/api/generated";
import { useUrlListState } from "@app/util/use-url-list-state";

import { getPlatformStatuses, listRuns, RUNS_PAGE_SIZE } from "./product-page-sync-api";

const POLL_INTERVAL_MS = 4000;

interface IUseSyncDashboard {
  platforms: PlatformSyncStatusDTO[];
  runs: SyncRunResultDTO[];
  isLoading: boolean;
  currentPage: number;
  /** Total runs matching the current filters (filter-aware; independent of the current page). */
  total: number;
  totalPages: number;
  statusFilter?: ListRunsStatusEnum;
  platformFilter?: number;
  /** True while any platform is RUNNING — drives polling and the "sync all" disable. */
  anyRunning: boolean;
  refresh: () => Promise<void>;
  setPlatforms: Dispatch<SetStateAction<PlatformSyncStatusDTO[]>>;
  setCurrentPage: (page: number) => void;
  setStatusFilter: (status?: ListRunsStatusEnum) => void;
  setPlatformFilter: (platformId?: number) => void;
}

const isValidStatus = (value: string | null): value is ListRunsStatusEnum =>
  value !== null && (Object.values(ListRunsStatusEnum) as string[]).includes(value);

/**
 * Loads the dashboard data — the platform-status aggregate (sidebar) plus a filtered, paginated
 * page of runs (table) — and polls both every few seconds while anything is RUNNING (P1). Polling
 * stops once everything is idle. The DB is the source of truth for `isRunning`, so this stays
 * correct even if a sync was triggered elsewhere or a blocking trigger request timed out.
 *
 * Page + filters live in the URL query (`?page`/`?status`/`?platform`) so the exact view is
 * restored when navigating back from a run's detail page.
 */
export const useSyncDashboard = (): IUseSyncDashboard => {
  const { page: currentPage, filters, setPage, setFilters } = useUrlListState(["status", "platform"] as const);
  const [platforms, setPlatforms] = useState<PlatformSyncStatusDTO[]>([]);
  const [runs, setRuns] = useState<SyncRunResultDTO[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const statusParam = filters.status ?? null;
  const statusFilter = isValidStatus(statusParam) ? statusParam : undefined;
  const platformParam = Number(filters.platform);
  const platformFilter = Number.isInteger(platformParam) && platformParam > 0 ? platformParam : undefined;

  // Guards refresh() against setState-after-unmount: a fetch that resolves after the user
  // navigated away (the poll interval itself is already cleaned up) must not touch state. Set on
  // mount as well, so a remount of the same instance (StrictMode) does not freeze the view.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const [statuses, runsPage] = await Promise.all([
      getPlatformStatuses(),
      listRuns({ page: currentPage, platformId: platformFilter, status: statusFilter })
    ]);
    if (!mountedRef.current) {
      return;
    }
    setPlatforms(statuses);
    setRuns(runsPage.items);
    setTotal(runsPage.total);
  }, [currentPage, platformFilter, statusFilter]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    (async () => {
      try {
        await refresh();
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const anyRunning = platforms.some((platform) => platform.isRunning);

  useEffect(() => {
    if (!anyRunning) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      // Swallow transient poll errors — the next tick recovers.
      refresh().catch(() => undefined);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [anyRunning, refresh]);

  const setStatusFilter = (status?: ListRunsStatusEnum) => setFilters({ status });
  const setPlatformFilter = (platformId?: number) => setFilters({ platform: platformId });

  return {
    platforms,
    runs,
    isLoading,
    currentPage,
    total,
    totalPages: Math.ceil(total / RUNS_PAGE_SIZE),
    statusFilter,
    platformFilter,
    anyRunning,
    refresh,
    setPlatforms,
    setCurrentPage: setPage,
    setStatusFilter,
    setPlatformFilter
  };
};
